// interface into cookies/local/session storage

import { trigger, KeyedTrigger, TriggerCache, TriggerValue } from "./trigger";
import { pass } from "./types";

export type TriggerPersist<T> = TriggerValue<T> & {
    clear: ()=>void
}
export type TriggerStorage = TriggerCache<any> & {
    clear: (key: any)=>void,
    persist: <T,>(key: any, options?: {
        default?: T, defaulter?: ()=>T, extract?: (raw: unknown)=>T, save?: (value: T)=>unknown,
    })=>TriggerPersist<T>,
}
const triggerStorage = (storage: Storage): TriggerStorage => {
    const cache = trigger.cache<any>() // use cache but override get/set
    const rawChanges: KeyedTrigger<any> = (cache as any)._.keyed
    const bindings = new Map<string, any>()
    const pending = new Map<string, unknown>() // Keep this visit usable if storage is blocked/full.
    const none = Symbol('no storage update')
    let owner
    owner = Object.assign({}, cache, {
        storage,
        get: key => {
            key = String(key)
            if (pending.has(key)) return pending.get(key)
            try {
                const str = storage.getItem(key)
                return str ? JSON.parse(str) : fetchCookie(key)
            } catch { return undefined } // Preserve malformed bytes for recovery.
        },
        _set: function(key, value) {
            key = String(key)
            try {
                value === undefined ? storage.removeItem(key) : storage.setItem(key, JSON.stringify(value))
                pending.delete(key)
            } catch { pending.set(key, value) }
        },
        set: function(key, value=undefined) {
            key = String(key)
            const previous = this.get(key)
            const binding = bindings.get(key)
            if (previous === value && !(value === undefined && binding)) return
            this._set(key, value)
            if (binding) {
                const decoded = value == null ? binding.reset() : binding.extract(value)
                binding.origin = decoded
                try { binding.set(decoded) } finally { binding.origin = none }
            }
            // Raw observers always receive the stored representation.
            rawChanges.trigger(key, value, previous)
        },
        clear: function(key) {
            return this.set(key, undefined)
        },
        persist: function <T,>(key, options={}) {
            key = String(key)
            const existing = bindings.get(key)
            if (existing) {
                // Repeated render-time construction updates policy, never adds
                // another writer or reparses/replaces the current object.
                if (Object.hasOwn(options, 'save')) existing.save = options['save'] || pass
                if (Object.hasOwn(options, 'extract')) existing.extract = options['extract'] || pass
                if (Object.hasOwn(options, 'default')) existing.default = options['default']
                if (Object.hasOwn(options, 'defaulter')) existing.defaulter = options['defaulter'] || pass
                return existing.value as TriggerPersist<T>
            }
            // @ts-ignore
            const { default: _default, defaulter=pass, extract=pass, save=pass } = options
            const value = trigger.value<T>()
            const binding = { value, set: value.set.bind(value), save, extract,
                default: _default, defaulter, origin: none as unknown,
                reset: () => binding.default ?? binding.defaulter(),
            }
            const initial = this.get(key)
            binding.set(initial == null ? binding.reset() : extract(initial))
            bindings.set(key, binding)
            value.add(current => {
                if (binding.origin !== none && Object.is(binding.origin, current)) { binding.origin = none; return }
                if (current === undefined) { this.set(key, undefined); return }
                const raw = binding.save(current)
                // Undefined from a codec means temporary/non-persisted state
                // (e.g. Wordbase tutorial). Explicit set(undefined)/clear erase.
                if (raw === undefined) return
                const previous = this.get(key)
                this._set(key, raw)
                rawChanges.trigger(key, raw, previous)
            })
            return Object.assign(value, {
                clear: () => this.clear(key),
            })
        },
    })
    return owner
}

// no longer using actual cookies, but still keeping these around
function fetchCookie(name) {
    const namedCookie = document.cookie
        .split(';').reverse()
        .map(cookie => cookie.trim())
        .find(cookie => cookie.startsWith(String(name)+'='));
    const cookieJson = namedCookie?.split('=')[1]
    return cookieJson ? JSON.parse(cookieJson) : undefined;
}
function saveCookie(name, value, str=false) {
    // save cookie for ten years
    document.cookie = `${name}=${str ? value : JSON.stringify(value)};expires=${60*60*24*365*10};domain=${location.host.split('.').slice(-2).join('.')}`;
}
function clearCookie(name) {
    document.cookie = `${name}=;expires=0`;
}

const local = triggerStorage(localStorage)
export const store = Object.assign(local, {
    local,
    session: triggerStorage(sessionStorage),
    cookie: {
        clear: clearCookie,
    },
    memo: trigger.store(),
})
